// Lists and pins in the web app: the picker (from a message's hover bar, its phone menu, the composer), the badges
// under a message, the quiet line ("Sam pinned a message"), the list panel, and the new-list dialog.
// The model is shared/src/lists.ts; Room (realtime.ts) keeps the lists and does the changes.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { canAddTo, canManageList, canRemoveFrom, cleanListName, listLabel, MAX_LIST_ADDERS, PINS, type ListActor, type ListAddPolicy, type ListInfo, type ListItem, type Message } from "@bloom/shared";
import { isNarrow } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";
import { Avatar } from "./ui.tsx";
import { CloseButton } from "./icons.tsx";
import { ActionMenu, Confirm } from "./Confirm.tsx";
import { Markdown } from "./Markdown.tsx";
import { Attachments } from "./Attachments.tsx";
import "./lists.css";

// ---- Icons ----

export function PinIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 17v5" />
      <path d="M9 10.8V5h6v5.8l2.6 3.2a1 1 0 0 1-.8 1.6H7.2a1 1 0 0 1-.8-1.6z" />
      <path d="M8 3h8" />
    </svg>
  );
}

/** A bookmark with a plus: "add to a collection". */
export function ListAddIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 13.5V21l-5-3.5L7 21V5a2 2 0 0 1 2-2h4" />
      <path d="M18 3v6M15 6h6" />
    </svg>
  );
}

export function LockIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function PlainListIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 3H7a2 2 0 0 0-2 2v16l7-4 7 4V5a2 2 0 0 0-2-2z" />
    </svg>
  );
}

/** A list's emoji, the pin for pins, or a plain bookmark. */
export function ListGlyph({ list, size = 16 }: { list: Pick<ListInfo, "kind" | "emoji">; size?: number }) {
  if (list.kind === "pins") return <span className="list-glyph"><PinIcon size={size} /></span>;
  if (list.emoji) return <span className="list-glyph emoji" style={{ fontSize: size }}>{list.emoji}</span>;
  return <span className="list-glyph"><PlainListIcon size={size} /></span>;
}

// ---- Which lists, in which order ----

const recentKey = (serverId: string) => `bloom:recent-lists:${serverId}`;

/** Lists used lately on this device in this server, newest first. */
export function recentLists(serverId: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(recentKey(serverId)) ?? "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function noteListUse(serverId: string, listId: string) {
  if (listId === PINS) return;
  try {
    localStorage.setItem(recentKey(serverId), JSON.stringify([listId, ...recentLists(serverId).filter((x) => x !== listId)].slice(0, 12)));
  } catch {
    // private mode: no recents, that's fine
  }
}

/**
 * The named lists a message from this conversation can go in, recently used first, then by name: shared lists
 * (never for a DM's messages; with `actor`, only ones they may add to, plus any in `keep`) and my private lists.
 * Pins aren't here: they're always offered on their own.
 */
export function namedLists(lists: ListInfo[], serverId: string, inDm: boolean, actor?: ListActor, keep?: Set<string>): ListInfo[] {
  const recent = recentLists(serverId);
  const rank = (l: ListInfo) => {
    const i = recent.indexOf(l.id);
    return i < 0 ? recent.length : i;
  };
  return lists
    .filter((l) => l.kind === "custom" && (l.visibility === "private" || !inDm))
    .filter((l) => !actor || canAddTo(l, actor) || !!keep?.has(l.id))
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** Lists whose name (or a word in it) starts with the query, then ones that contain it. */
export function matchLists(lists: ListInfo[], q: string): ListInfo[] {
  const s = q.trim().toLowerCase();
  if (!s) return lists;
  const starts = lists.filter((l) => l.name.toLowerCase().startsWith(s) || l.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(s)));
  return [...starts, ...lists.filter((l) => !starts.includes(l) && l.name.toLowerCase().includes(s))];
}

/** "pin", "pinned", "pins": the query means the pins. */
export const meansPins = (q: string) => q.trim().length > 0 && "pinned".startsWith(q.trim().toLowerCase().replace(/s$/, ""));

// ---- The picker ----

/** Where a floating panel opened from a button goes: on screen, beside the button (like the emoji picker). */
function placeBy(anchor: DOMRect, w: number, h: number): React.CSSProperties {
  const edge = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const below = anchor.bottom + 4;
  const above = anchor.top - 4 - h;
  const top = below + h <= vh - edge ? below : above >= edge ? above : Math.max(edge, vh - edge - h);
  const left = Math.min(Math.max(edge, anchor.right - w), vw - edge - w);
  return { position: "fixed", top, left };
}

const PICKER_W = 300;
const PICKER_H = 380;

/**
 * Pick lists for a message (or a message being written): Pin at the top, then the named lists with a check for
 * each one it's in, a search box that's also how you make a new list ("Create “Recipes”"). A floating panel by
 * the button on a computer, a bottom sheet on a phone.
 */
export function ListPicker({
  serverId,
  lists,
  checked,
  pinned,
  canPin = true,
  inDm,
  onToggle,
  onCreate,
  onClose,
  anchor,
  title = "Add to collection",
  actor,
}: {
  serverId: string;
  /** Every list I can see (the picker chooses which fit). */
  lists: ListInfo[];
  /** Named lists the message is in. */
  checked: Set<string>;
  pinned: boolean;
  canPin?: boolean;
  /** The message is in a DM: shared lists aren't offered. */
  inDm: boolean;
  onToggle: (listId: string, on: boolean) => void;
  /** Makes a list (and the caller puts the message in it). Resolves with an error to show, or null. */
  onCreate: (name: string, visibility: "server" | "private") => Promise<string | null>;
  onClose: () => void;
  anchor?: DOMRect | null;
  title?: string;
  /** Who's picking: shared lists locked to other people aren't offered (unless the message is already in one). */
  actor?: ListActor;
}) {
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const sheet = isNarrow();

  const named = useMemo(() => namedLists(lists, serverId, inDm, actor, checked), [lists, serverId, inDm, actor?.userId, actor?.role, checked]);
  const shown = matchLists(named, q);
  const name = cleanListName(q);
  const exact = named.some((l) => l.name.toLowerCase() === name.toLowerCase());
  const showPin = canPin && (!q.trim() || meansPins(q));
  type Row = { kind: "pin" } | { kind: "list"; list: ListInfo } | { kind: "create"; visibility: "server" | "private" };
  const rows: Row[] = [
    ...(showPin ? [{ kind: "pin" } as Row] : []),
    ...shown.map((list) => ({ kind: "list", list }) as Row),
    ...(name && !exact ? [...(!inDm ? [{ kind: "create", visibility: "server" } as Row] : []), { kind: "create", visibility: "private" } as Row] : []),
  ];
  const at = Math.min(index, Math.max(0, rows.length - 1));

  useEffect(() => {
    if (sheet) return;
    const onDown = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && onClose();
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
    };
  }, []);
  useLayoutEffect(() => {
    if (!sheet) input.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => setIndex(0), [q]);
  useEffect(() => {
    box.current?.querySelector(".lp-row.active")?.scrollIntoView({ block: "nearest" });
  }, [at]);

  const run = async (r: Row) => {
    setError(null);
    if (r.kind === "pin") return onToggle(PINS, !pinned);
    if (r.kind === "list") {
      noteListUse(serverId, r.list.id);
      return onToggle(r.list.id, !checked.has(r.list.id));
    }
    setBusy(true);
    const problem = await onCreate(name, r.visibility);
    setBusy(false);
    if (problem) setError(problem);
    else setQ("");
  };

  const panel = (
    <div
      className={`list-picker${sheet ? " in-sheet" : ""}`}
      ref={box}
      style={sheet || !anchor ? undefined : placeBy(anchor, PICKER_W, PICKER_H)}
      role="dialog"
      aria-label={title}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          const n = rows.length || 1;
          setIndex((at + (e.key === "ArrowDown" ? 1 : n - 1)) % n);
        } else if (e.key === "Enter" && rows[at] && !busy) {
          e.preventDefault();
          void run(rows[at]!);
        }
      }}
    >
      <div className="lp-head">
        <strong>{title}</strong>
        {!sheet && <CloseButton size="sm" onClick={onClose} aria-label="Close" />}
      </div>
      <div className="lp-search">
        <label htmlFor="lp-q" className="sr-only">Find or create a collection</label>
        <input id="lp-q" ref={input} className="input" placeholder="Find or create a collection" value={q} maxLength={60} autoComplete="off" onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="lp-rows" role="listbox" aria-label="Collections" aria-multiselectable="true">
        {rows.map((r, i) => {
          const active = i === at ? " active" : "";
          if (r.kind === "pin") {
            return (
              <button key="pin" type="button" role="option" aria-selected={pinned} className={`lp-row${active}`} onMouseEnter={() => setIndex(i)} onClick={() => void run(r)}>
                <span className="lp-glyph"><PinIcon /></span>
                <span className="lp-name">{pinned ? "Pinned here" : "Pin to this conversation"}</span>
                <Check on={pinned} />
              </button>
            );
          }
          if (r.kind === "list") {
            const on = checked.has(r.list.id);
            return (
              <button key={r.list.id} type="button" role="option" aria-selected={on} className={`lp-row${active}`} onMouseEnter={() => setIndex(i)} onClick={() => void run(r)}>
                <span className="lp-glyph"><ListGlyph list={r.list} /></span>
                <span className="lp-name">{r.list.name}</span>
                {r.list.visibility === "private" && <span className="lp-meta" title="Only you can see this collection"><LockIcon /></span>}
                {r.list.addPolicy === "chosen" && <span className="lp-meta lp-locked" title="Only some people can add to this collection">Locked</span>}
                <Check on={on} />
              </button>
            );
          }
          return (
            <button key={`new-${r.visibility}`} type="button" role="option" aria-selected={false} className={`lp-row lp-create${active}`} disabled={busy} onMouseEnter={() => setIndex(i)} onClick={() => void run(r)}>
              <span className="lp-glyph">+</span>
              <span className="lp-name">
                Create “{name}”
                <span className="lp-sub">{r.visibility === "server" ? "Shared with everyone here" : "Only you"}</span>
              </span>
              {r.visibility === "private" && <span className="lp-meta"><LockIcon /></span>}
            </button>
          );
        })}
        {!rows.length && <div className="lp-empty muted">No collections yet. Type a name to make one.</div>}
        {!q && !named.length && rows.length > 0 && <div className="lp-empty muted">Type a name to make a collection.</div>}
        {inDm && <div className="lp-note muted">Messages from a DM can go in its pins or your private collections.</div>}
      </div>
      {error && <div className="lp-error error" role="alert">{error}</div>}
    </div>
  );
  if (sheet) return <Sheet label={title} onClose={onClose}>{panel}</Sheet>;
  return anchor ? createPortal(panel, document.body) : panel;
}

function Check({ on }: { on: boolean }) {
  return (
    <span className={`lp-check${on ? " on" : ""}`} aria-hidden="true">
      {on && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5 10 17 19 7" /></svg>
      )}
    </span>
  );
}

// ---- On a message ----

/** Little chips under a message for the lists it's in (that I can see): "📌 Pinned", "🍝 Recipes". */
export function ListBadges({ ids, lists, onOpen }: { ids: string[]; lists: Map<string, ListInfo>; onOpen: (listId: string) => void }) {
  const known = ids.map((id) => lists.get(id)).filter((l): l is ListInfo => !!l).sort((a, b) => Number(b.kind === "pins") - Number(a.kind === "pins"));
  if (!known.length) return null;
  return (
    <div className="list-badges">
      {known.map((l) => (
        <button key={l.id} type="button" className={`list-badge${l.kind === "pins" ? " pins" : ""}`} onClick={() => onOpen(l.id)} title={l.kind === "pins" ? "Pinned: see all pins" : `In ${l.name}${l.visibility === "private" ? " (only you)" : ""}`}>
          <ListGlyph list={l} size={12} />
          <span>{listLabel(l)}</span>
          {l.visibility === "private" && <LockIcon size={10} />}
        </button>
      ))}
    </div>
  );
}

/** "Sam pinned a message and added it to 🍝 Recipes." A quiet line with links to the message and the lists. */
export function SystemLine({ m, who, lists, onOpenList, onJump }: { m: Message; who: string; lists: Map<string, ListInfo>; onOpenList: (id: string) => void; onJump: (messageId: string) => void }) {
  const ids = m.system?.lists ?? [];
  const pins = ids.map((id) => lists.get(id)).find((l) => l?.kind === "pins");
  const named = ids.filter((id) => lists.get(id)?.kind !== "pins");
  const link = (id: string) => {
    const l = lists.get(id);
    return l ? (
      <button key={id} type="button" className="sys-link" onClick={() => onOpenList(id)}>
        {l.emoji ? `${l.emoji} ` : ""}{l.name}
      </button>
    ) : (
      <em key={id}>a collection that's gone</em>
    );
  };
  const joined: ReactNode[] = [];
  named.forEach((id, i) => {
    if (i > 0) joined.push(i === named.length - 1 ? " and " : ", ");
    joined.push(link(id));
  });
  const message = (
    <button type="button" className="sys-link" onClick={() => onJump(m.system!.messageId)}>
      a message
    </button>
  );
  const wasPinned = !!pins;
  return (
    <div className="sys-line" role="note">
      <span className="sys-icon">{wasPinned ? <PinIcon size={14} /> : <ListAddIcon size={14} />}</span>
      <span className="sys-text">
        <strong>{who}</strong>{" "}
        {wasPinned ? (
          <>
            pinned {message}
            {named.length > 0 && <> and added it to {joined}</>}.{" "}
            <button type="button" className="sys-link muted-link" onClick={() => onOpenList(pins!.id)}>See all pins</button>
          </>
        ) : (
          <>
            added {message} to {named.length ? joined : <em>a collection</em>}.
          </>
        )}
      </span>
    </div>
  );
}

// ---- A new list ----

export function NewListDialog({ onCreate, onClose }: { onCreate: (name: string, emoji: string, visibility: "server" | "private") => Promise<string | null>; onClose: () => void }) {
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("");
  const [visibility, setVisibility] = useState<"server" | "private">("server");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal new-list"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nl-title"
        style={{ maxWidth: 420 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!cleanListName(name) || busy) return;
          setBusy(true);
          const problem = await onCreate(name, emoji, visibility);
          setBusy(false);
          if (problem) setError(problem);
          else onClose();
        }}
      >
        <h2 id="nl-title">New collection</h2>
        <div className="nl-row">
          <div className="field nl-emoji">
            <label htmlFor="nl-emoji">Emoji</label>
            <input id="nl-emoji" className="input" value={emoji} maxLength={8} placeholder="🍝" onChange={(e) => setEmoji(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="nl-name">Name</label>
            <input id="nl-name" className="input" autoFocus value={name} maxLength={40} placeholder="Recipes" onChange={(e) => setName(e.target.value)} />
          </div>
        </div>
        <fieldset className="nl-vis">
          <legend className="sr-only">Who can see it</legend>
          <label className={`nl-choice${visibility === "server" ? " on" : ""}`}>
            <input type="radio" name="nl-vis" checked={visibility === "server"} onChange={() => setVisibility("server")} />
            <span>
              <strong>Shared</strong>
              <span className="muted">Everyone here can see it and add to it.</span>
            </span>
          </label>
          <label className={`nl-choice${visibility === "private" ? " on" : ""}`}>
            <input type="radio" name="nl-vis" checked={visibility === "private"} onChange={() => setVisibility("private")} />
            <span>
              <strong>Only me</strong>
              <span className="muted">A private collection. Nobody else sees it or what's in it.</span>
            </span>
          </label>
        </fieldset>
        {error && <p className="error" role="alert" style={{ margin: 0 }}>{error}</p>}
        <div className="confirm-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={!cleanListName(name) || busy}>{busy ? "Making it…" : "Create collection"}</button>
        </div>
      </form>
    </div>
  );
}

// ---- The panel ----

export interface ListSource {
  listItems: (listId: string, before?: number) => Promise<{ items: ListItem[]; messages: Message[]; more: boolean } | { error: string }>;
  setListItem: (m: Pick<Message, "id" | "channelId">, listId: string, on: boolean) => Promise<string | null>;
  updateList: (listId: string, patch: { name?: string; emoji?: string; addPolicy?: ListAddPolicy; adders?: string[] }) => Promise<string | null>;
  deleteList: (listId: string) => Promise<string | null>;
}

/**
 * A list beside the chat (full screen on a phone): its messages, newest added first, each with where it's from, a
 * Jump button and a way to take it out. `list` is undefined for a channel whose pins were never used.
 */
export function ListPanel({
  list,
  emptyLabel,
  source,
  names,
  me,
  isAdmin,
  pinsInDm = false,
  where,
  plain,
  onJump,
  onClose,
  panelRef,
}: {
  list: ListInfo | undefined;
  /** What the panel is for when there's no list yet ("#general"). */
  emptyLabel: string;
  source: ListSource;
  names: Map<string, string>;
  me: string | null;
  isAdmin: boolean;
  /** These are a DM's pins (any of its members may unpin). */
  pinsInDm?: boolean;
  /** "#general", "Maya", "thread in #general": where a message lives. */
  where: (stream: string, rootId: string) => string;
  /** Stored text as typed ("@Maya", "#general"). */
  plain: (text: string) => string;
  onJump: (item: ListItem) => void;
  onClose: () => void;
  panelRef?: React.Ref<HTMLElement>;
}) {
  const [items, setItems] = useState<ListItem[]>([]);
  const [messages, setMessages] = useState<Map<string, Message>>(new Map());
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(!!list);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ name: string; emoji: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [locking, setLocking] = useState(false);
  const listId = list?.id;
  // A fresh first page whenever the list changes (someone added or took something out).
  const version = list ? `${list.id}:${list.updatedAt}:${list.count}` : "";
  useEffect(() => {
    if (!listId) {
      setItems([]);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    const t = setTimeout(async () => {
      const r = await source.listItems(listId);
      if (!live) return;
      setLoading(false);
      if ("error" in r) return setError(r.error);
      setError(null);
      setItems(r.items);
      setMessages(new Map(r.messages.map((m) => [m.id, m])));
      setMore(r.more);
    }, 120);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [version]);

  const loadMore = async () => {
    if (!listId || !more || loading) return;
    setLoading(true);
    const r = await source.listItems(listId, items.at(-1)?.addedAt);
    setLoading(false);
    if ("error" in r) return setError(r.error);
    setItems((x) => [...x, ...r.items.filter((it) => !x.some((y) => y.messageId === it.messageId))]);
    setMessages((x) => new Map([...x, ...r.messages.map((m) => [m.id, m] as const)]));
    setMore(r.more);
  };

  // The rules are shared with the room (shared/src/lists.ts); owner and admin are the same to them.
  const actor: ListActor = { userId: me ?? "", role: isAdmin ? "admin" : "member" };
  const editable = !!list && canManageList(list, actor);
  const shared = list?.kind === "custom" && list.visibility === "server";
  const title = list ? listLabel(list) : "Pinned";
  const subtitle = !list
    ? emptyLabel
    : list.kind === "pins"
      ? `${where(list.channelId!, list.channelId!)} · ${list.count} ${list.count === 1 ? "message" : "messages"}`
      : `${list.visibility === "private" ? "Only you" : `Shared · made by ${list.createdBy === me ? "you" : (names.get(list.createdBy) ?? "someone")}${list.addPolicy === "chosen" ? " · locked" : ""}`} · ${list.count} ${list.count === 1 ? "message" : "messages"}`;

  return (
    <aside className="list-panel" aria-label={title} ref={panelRef}>
      <div className="thread-head list-head">
        <button className="icon-btn mobile-only head-btn" onClick={onClose} aria-label="Back to chat">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <span className="list-head-glyph"><ListGlyph list={list ?? { kind: "pins", emoji: "" }} size={18} /></span>
        <div style={{ minWidth: 0, flex: 1 }}>
          {renaming ? (
            <form
              className="list-rename"
              onSubmit={async (e) => {
                e.preventDefault();
                const problem = await source.updateList(list!.id, { name: renaming.name, emoji: renaming.emoji });
                if (problem) setNote(problem);
                else setRenaming(null);
              }}
            >
              <input className="input" aria-label="Emoji" value={renaming.emoji} maxLength={8} style={{ width: 52 }} onChange={(e) => setRenaming({ ...renaming, emoji: e.target.value })} />
              <input className="input" aria-label="Collection name" autoFocus value={renaming.name} maxLength={40} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} onKeyDown={(e) => e.key === "Escape" && setRenaming(null)} />
              <button type="submit" className="btn btn-primary btn-small">Save</button>
            </form>
          ) : (
            <>
              <div className="list-title">
                {title}
                {list?.visibility === "private" && <span className="muted" title="Only you can see this collection"> <LockIcon /></span>}
              </div>
              <div className="muted list-sub">{subtitle}</div>
            </>
          )}
        </div>
        {editable && !renaming && (
          <ActionMenu
            label="Collection options"
            actions={[
              { label: "Rename", onSelect: () => setRenaming({ name: list!.name, emoji: list!.emoji }) },
              ...(shared ? [{ label: "Who can add…", onSelect: () => setLocking(true) }] : []),
              { label: "Delete collection", danger: true, onSelect: () => setDeleting(true) },
            ]}
          />
        )}
        <CloseButton className="desktop-only" onClick={onClose} aria-label="Close collection" />
      </div>
      {note && (
        <div className="command-note" role="alert">
          {note}
          <CloseButton size="sm" aria-label="Dismiss" onClick={() => setNote(null)} />
        </div>
      )}
      <div
        className="list-items"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) void loadMore();
        }}
      >
        {error && <div className="list-empty error">{error}</div>}
        {!error && !loading && !items.length && (
          <div className="list-empty">
            <div className="list-empty-glyph"><ListGlyph list={list ?? { kind: "pins", emoji: "" }} size={28} /></div>
            {list?.kind === "custom" ? (
              <p>Nothing in {list.name} yet. Hover a message and choose <strong>Add to collection</strong>, or type <kbd>+</kbd> while you write.</p>
            ) : (
              <p>Nothing pinned in {list ? where(list.channelId!, list.channelId!) : emptyLabel} yet. Hover a message and choose <strong>Pin</strong> (or press <kbd>P</kbd>).</p>
            )}
          </div>
        )}
        {items.map((it) => {
          const m = messages.get(it.messageId);
          const author = m ? (names.get(m.authorId) ?? (m.authorId === "bloom" ? "Bloom" : "Someone")) : "Someone";
          return (
            <article key={it.messageId} className="list-item">
              <div className="li-head">
                {m && <Avatar id={m.authorId} name={author} size={22} />}
                <strong>{author}</strong>
                <span className="muted li-where">{where(it.channelId, it.rootId)}</span>
                {m && <time className="muted" dateTime={new Date(m.ts).toISOString()}>{dateFmt.format(m.ts)}</time>}
              </div>
              {m ? (
                <>
                  {m.body && (
                    <div className="li-body body">
                      <Markdown source={m.body} text={(t) => plain(t)} />
                    </div>
                  )}
                  {m.attachments && <Attachments messageId={m.id} attachments={m.attachments} onOpen={(src) => window.open(src, "_blank", "noopener")} />}
                </>
              ) : (
                <div className="li-body muted"><em>Message unavailable</em></div>
              )}
              <div className="li-actions">
                <button type="button" className="btn btn-small" onClick={() => onJump(it)}>Jump</button>
                <span className="muted li-added">
                  {list?.kind === "pins" ? "Pinned" : "Added"} by {it.addedBy === me ? "you" : (names.get(it.addedBy) ?? "someone")}
                </span>
                {list && canRemoveFrom(list, it.addedBy, actor, pinsInDm) && <button
                  type="button"
                  className="icon-btn li-remove"
                  aria-label={list?.kind === "pins" ? "Unpin" : `Remove from ${list?.name ?? "collection"}`}
                  title={list?.kind === "pins" ? "Unpin" : "Remove from collection"}
                  onClick={async () => {
                    setItems((x) => x.filter((y) => y.messageId !== it.messageId));
                    const problem = await source.setListItem({ id: it.messageId, channelId: it.channelId }, list!.id, false);
                    if (problem) setNote(problem);
                  }}
                >
                  <CloseIconSmall />
                </button>}
              </div>
            </article>
          );
        })}
        {loading && <div className="list-empty muted">Loading…</div>}
      </div>
      {locking && list && (
        <WhoCanAddDialog
          list={list}
          names={names}
          me={me}
          onSave={(addPolicy, adders) => source.updateList(list.id, { addPolicy, adders })}
          onClose={() => setLocking(false)}
        />
      )}
      {deleting && list && (
        <Confirm title={`Delete ${list.name}?`} confirmLabel="Delete collection" danger onConfirm={() => source.deleteList(list.id)} onClose={() => setDeleting(false)}>
          <p style={{ margin: 0 }}>The collection goes{list.visibility === "server" ? " for everyone" : ""}. The messages in it stay where they are.</p>
        </Confirm>
      )}
    </aside>
  );
}

/**
 * A shared list's add setting: everyone, or only chosen people (its creator, owners and admins always can). Taking
 * things out doesn't change: whoever added it, the creator, or an owner or admin.
 */
export function WhoCanAddDialog({ list, names, me, onSave, onClose }: { list: ListInfo; names: Map<string, string>; me: string | null; onSave: (policy: ListAddPolicy, adders: string[]) => Promise<string | null>; onClose: () => void }) {
  const [policy, setPolicy] = useState<ListAddPolicy>(list.addPolicy ?? "everyone");
  const [chosen, setChosen] = useState<Set<string>>(new Set(list.adders ?? []));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const people = [...names].filter(([id]) => id !== list.createdBy && id !== "bloom").sort((a, b) => a[1].localeCompare(b[1]));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal new-list who-can-add"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wca-title"
        style={{ maxWidth: 420 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy) return;
          setBusy(true);
          const problem = await onSave(policy, policy === "chosen" ? [...chosen] : []);
          setBusy(false);
          if (problem) setError(problem);
          else onClose();
        }}
      >
        <h2 id="wca-title">Who can add to {list.name}</h2>
        <fieldset className="nl-vis">
          <legend className="sr-only">Who can add</legend>
          <label className={`nl-choice${policy === "everyone" ? " on" : ""}`}>
            <input type="radio" name="wca-policy" checked={policy === "everyone"} onChange={() => setPolicy("everyone")} />
            <span>
              <strong>Everyone</strong>
              <span className="muted">Anyone here can add messages.</span>
            </span>
          </label>
          <label className={`nl-choice${policy === "chosen" ? " on" : ""}`}>
            <input type="radio" name="wca-policy" checked={policy === "chosen"} onChange={() => setPolicy("chosen")} />
            <span>
              <strong>Only people I choose</strong>
              <span className="muted">Plus {list.createdBy === me ? "you" : (names.get(list.createdBy) ?? "whoever made it")}, owners and admins. Everyone can still see it.</span>
            </span>
          </label>
        </fieldset>
        {policy === "chosen" && (
          <div className="wca-people" role="group" aria-label="People who can add">
            {people.map(([id, name]) => (
              <label key={id} className="wca-person">
                <input
                  type="checkbox"
                  checked={chosen.has(id)}
                  disabled={!chosen.has(id) && chosen.size >= MAX_LIST_ADDERS}
                  onChange={(e) => {
                    const next = new Set(chosen);
                    if (e.target.checked) next.add(id);
                    else next.delete(id);
                    setChosen(next);
                  }}
                />
                <Avatar id={id} name={name} size={22} />
                <span>{name}</span>
              </label>
            ))}
            {!people.length && <p className="muted" style={{ margin: 0 }}>Nobody else is here yet.</p>}
          </div>
        )}
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Whoever added a message, the collection's creator, owners and admins can take it out.</p>
        {error && <p className="error" role="alert" style={{ margin: 0 }}>{error}</p>}
        <div className="confirm-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

const dateFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function CloseIconSmall() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
  );
}
